import { toast } from '@bill-alps/ui';
import { ConvexError } from 'convex/values';
import type { TFunction } from 'i18next';
import { markStaleBundle } from '@/lib/deploy-skew/stale-bundle';

type ErrorCodeMap = Record<string, string>;

const DEFAULT_ERROR_CODES: ErrorCodeMap = {
  PAYSLIP_HAS_LEDGER_HISTORY: 'payslips:errors.ledgerHistory',
  EXPENSE_DRAFT_LIMIT_REACHED: 'supplierBills:errors.draftLimitReached',
  PAYROLL_AVS_ANNUAL_CHOICE_REQUIRED: 'employees:avs.errors.history',
  PAYROLL_AVS_SUSPENDED: 'employees:avs.errors.suspended',
  PAYROLL_AVS_IDENTITY_REQUIRED: 'employees:avs.errors.identity',
  PAYROLL_AVS_IDENTITY_CONFLICT: 'employees:avs.errors.conflict',
  PAYROLL_AVS_CHOICE_UNKNOWN: 'employees:avs.errors.history',
  PAYROLL_AVS_HISTORY_MISSING: 'employees:avs.errors.history',
  PAYROLL_AVS_OPENING_REQUIRED: 'employees:avs.errors.opening',
  PAYROLL_AVS_OPENING_INVALID: 'employees:avs.errors.opening',
  PAYROLL_AVS_OPENING_OVERLAP: 'employees:avs.errors.opening',
  PAYROLL_AVS_CHOICE_LOCKED: 'employees:avs.errors.locked',
  PAYROLL_AVS_CHOICE_INVALID: 'employees:avs.errors.invalid',
  PAYROLL_AVS_STALE: 'employees:avs.errors.stale',
  PAYROLL_AVS_HISTORY_REQUIRES_CORRECTION: 'employees:avs.errors.stale',
  PAYROLL_AVS_CONTRIBUTION_OVERRIDE_FORBIDDEN: 'employees:avs.errors.override',
  PAYROLL_LAA_HISTORY_INCOMPLETE: 'laa:errors.history',
  PAYROLL_LAA_HISTORY_REQUIRES_CORRECTION: 'laa:errors.history',
  PAYROLL_LAA_OPENING_SPLIT_REQUIRED: 'laa:errors.history',
  PAYROLL_LAA_OPENING_INVALID: 'laa:errors.history',
  PAYROLL_LAA_VARIABLE_SPLIT_REQUIRED: 'laa:errors.split',
  PAYROLL_LAA_SALARY_SPLIT_REQUIRED: 'laa:errors.split',
  PAYROLL_LAA_SPLIT_TOTAL: 'laa:errors.split',
  PAYROLL_LAA_SPLIT_INVALID: 'laa:errors.split',
  PAYROLL_LAA_ACQUISITION_INVALID: 'laa:errors.split',
  PAYROLL_LAA_ALLOCATION_REASON_REQUIRED: 'laa:errors.split',
  PAYROLL_LAA_STALE: 'laa:errors.stale',
  PAYROLL_LAA_COVERAGE_QUALIFICATION_REQUIRED: 'laa:errors.coverage',
  PAYROLL_LAA_EFFECTIVE_DATE_REQUIRED: 'laa:errors.date',
  PAYROLL_LAA_DATED_CHANGE_REQUIRED: 'laa:errors.date',
  PAYROLL_LAA_CORRECTION_ORIGIN_REQUIRED: 'laa:errors.origin',
  PAYROLL_LAA_CORRECTION_ORIGIN_INVALID: 'laa:errors.origin',
  PAYROLL_LAA_PERIOD_INVALID: 'laa:errors.period',
  PAYROLL_LAA_PERIOD_OVERLAP: 'laa:errors.period',
  PAYROLL_LAA_CONTRIBUTION_OVERRIDE_FORBIDDEN: 'laa:errors.override',
  PAYROLL_ACQUISITION_PERIOD_REQUIRED: 'payslips:errors.acquisitionRequired',
  PAYROLL_ACQUISITION_PERIOD_INVALID: 'payslips:errors.acquisitionInvalid',
  PAYROLL_ACQUISITION_SPLIT_REQUIRED: 'payslips:errors.acquisitionSplit',
  PAYROLL_ACQUISITION_RATES_MISSING: 'payslips:errors.acquisitionRates',
  PAYROLL_ACQUISITION_HISTORY_MISSING: 'payslips:errors.acquisitionHistory',
  PAYROLL_ACQUISITION_HISTORY_UNALLOCATED:
    'payslips:errors.acquisitionUnallocated',
  PAYROLL_ACQUISITION_AVS_HISTORY_REQUIRED:
    'payslips:errors.acquisitionAvsHistory',
  PAYROLL_ACQUISITION_RECOMPUTE_REQUIRED:
    'payslips:errors.acquisitionRecompute',
  PAYROLL_POST_EXIT_GROSS_OVERRIDE_FORBIDDEN:
    'payslips:errors.postExitGrossOverride',
  COMPANY_LEGAL_FORM_REQUIRED: 'common:classificationErrors.legalFormRequired',
  COMPANY_LEGAL_FORM_LOCKED: 'common:classificationErrors.legalFormLocked',
  COMPANY_CHART_REINITIALIZATION_REQUIRED:
    'common:classificationErrors.reinitialize',
  COMPANY_CORPORATE_FORM_REQUIRED: 'common:classificationErrors.corporateOnly',
  CLASSIFICATION_ACCOUNT_REQUIRED: 'common:classificationErrors.required',
  CLASSIFICATION_ACCOUNT_NOT_FOUND: 'common:classificationErrors.unavailable',
  CLASSIFICATION_ACCOUNT_UNUSABLE: 'common:classificationErrors.unavailable',
  CLASSIFICATION_ACCOUNT_INCOMPATIBLE:
    'common:classificationErrors.incompatible',
  LEDGER_RESULT_ALLOCATION_REQUIRED:
    'common:classificationErrors.allocationRequired',
  LEDGER_RESULT_ALLOCATION_MISMATCH:
    'common:classificationErrors.allocationMismatch',
};

/**
 * Convex's wording when the client calls a public function the deployment no
 * longer exposes. Always means the tab runs a bundle older than the backend.
 */
const STALE_BUNDLE_MARKER = 'Could not find public function';

/**
 * Whether a thrown Convex error is deploy skew rather than a feature error.
 * Exported for the callers that cannot use `handleMutationError` because they
 * render their failure inline (no `<Toaster />` on the auth screens) — they
 * still have to escalate this one to `markStaleBundle()`.
 */
export function isStaleBundleError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err);
  return message.includes(STALE_BUNDLE_MARKER);
}

/**
 * The text a code map can match against. Production Convex redacts the
 * message of a plain `Error` to "Server Error", so a backend code only
 * survives the round-trip when it is thrown as `ConvexError(code)` — that
 * payload comes back untouched in `data`.
 */
function matchableText(err: unknown, message: string): string {
  if (err instanceof ConvexError) {
    if (typeof err.data === 'string') return err.data;
    if (
      err.data &&
      typeof err.data === 'object' &&
      'code' in err.data &&
      typeof err.data.code === 'string'
    )
      return err.data.code;
  }
  return message;
}

/**
 * Handles mutation errors consistently:
 * - Escalates a deploy-skew error to the blocking "reload" dialog
 * - Maps known error codes to i18n keys via `codeMap`
 * - Falls back to the error message, or a generic i18n key
 */
export function handleMutationError(
  err: unknown,
  t: TFunction,
  codeMap?: ErrorCodeMap,
  fallbackKey?: string,
) {
  const message = err instanceof Error ? err.message : String(err);
  const text = matchableText(err, message);

  if (isStaleBundleError(err)) {
    // Not a feature error: the tab is broken until it reloads, so this raises
    // `StaleBundleDialog` rather than a toast that can be scrolled past.
    markStaleBundle();
    return;
  }

  for (const [code, i18nKey] of Object.entries({
    ...DEFAULT_ERROR_CODES,
    ...codeMap,
  })) {
    if (text.includes(code)) {
      toast.error(t(i18nKey));
      return;
    }
  }

  toast.error(fallbackKey ? t(fallbackKey) : text);
}
